import React from 'react';
import { useIntl } from 'react-intl';
import { percent } from 'shared/constants/math';
import Chart from './Chart';
import { getGridOptions, colorChartParts, uniqueSegmentId, getSegment, getValueAxis } from './utils';
import { tooltipFormatter, formatSegment } from './formatters';

const commonOptions = {
  type: 'line',
  smooth: true,
  showAllSymbol: true,
  symbol: 'circle',
  symbolSize: 10,
  connectNulls: true,
};

const LineChart = (props) => {
  const {
    segments,
    periods,
    question,
    isScoreGraph,
    toolbox,
    chartSettings = {},
  } = props;

  const { formatMessage, locale } = useIntl();
  const { type, displayMode } = chartSettings;

  let series = [];
  const showAsPercentage = displayMode === 'percentage' && !isScoreGraph;
  const areaOptions = type === 'area' ? { stack: 'stack', areaStyle: {} } : {};

  if (displayMode === 'score') {
    series = segments.map(segment => ({
      ...commonOptions,
      ...areaOptions,
      name: uniqueSegmentId(segment),
      color: segment.color,
      data: segment.periodScores,
      tooltip: {
        formatter: ({ value, dataIndex }) => {
          const formattedValue = tooltipFormatter({ data: { numericValue: value } });
          const formattedSegment = formatSegment(segment, { lineBreak: '<br />', includeDate: false, count: segment.periodCounts[dataIndex], formatMessage, locale });

          return `${periods[segment.dateRangeIndex][dataIndex]}: ${formattedValue}<br />${formattedSegment}`;
        },
      },
    }));
  } else {
    series = segments.filter(s => !s.isBenchmark).flatMap(segment => segment.values.map(({ id, name, data = [], optionValue }) => ({
      ...commonOptions,
      ...areaOptions,
      name: segment.values.length > 1 ? name : uniqueSegmentId(segment),
      optionValue, // For color
      lineStyle: {
        borderColor: segment.color,
        color: segment.color,
      },
      itemStyle: segment.values.length > 1 ? undefined : {
        color: segment.color,
      },
      data: data.map((v, i) => ({
        id,
        name,
        value: showAsPercentage ? percent(v, segment.periodCounts?.[i]) : v,
      })),
      tooltip: {
        formatter: ({ seriesName, dataIndex }) => {
          const value = data[dataIndex];
          const formattedValue = tooltipFormatter({ data: { numericValue: value, percentageValue: segment.periodCounts?.length && percent(value, segment.periodCounts?.[dataIndex]) } });
          const formattedSegment = formatSegment(segment, { lineBreak: '<br />', includeDate: false, count: segment.periodCounts?.length && segment.periodCounts?.[dataIndex], formatMessage, locale, ...props });

          return `${segment.values.length > 1 ? `${seriesName}<br />` : ''}${periods[segment.dateRangeIndex][dataIndex]}: ${formattedValue}<br />${formattedSegment}`;
        },
      },
    })));
    series = colorChartParts(question, series);
  }

  const legendFormatter = (value) => {
    const segment = getSegment(segments, value);
    if (!segment) return value;
    return formatSegment(segment, { formatMessage, locale });
  };

  const option = {
    grid: getGridOptions(),
    tooltip: {
      trigger: 'item',
    },
    legend: {
      data: [...new Set(series.map(s => s.name))],
      formatter: legendFormatter,
    },
    xAxis: {
      show: true,
      type: 'category',
      boundaryGap: false,
      data: periods[0],
    },
    yAxis: getValueAxis({
      showAsPercentage,
      adjustToValues: true,
    }),
    series,
  };

  return (
    <Chart
      {...props}
      option={option}
      toolbox={{
        ...toolbox,
        displayModeScoreLabel: isScoreGraph ? 'displayModeAverageScore' : undefined,
        displayModeValueLabel: isScoreGraph ? 'displayModeAllScores' : undefined,
        showScore: segments.some(s => s.score),
      }}
    />
  );
};

export default LineChart;
